import HomeColours from 'app/views/home/config/HomeColours';
import { device } from 'utils/device';

import {
	BEHAVIOUR_DISABLE_TIMELINE_X,
	BEHAVIOUR_PATH_ORBIT,
	BEHAVIOUR_PROGRESS_PARALLAX,
	FLAG_ACTIVE,
	PATH_FLAG_SCALE_ACTIVE,
	PATH_FLAG_SCALE_AFFECT,
	REPELL_PUSH
} from 'app/views/home/webgl/particlesOptimised/core/ParticleCore';
import { LOOKUP_CIRCLE, getPathLookups } from 'app/views/home/webgl/particlesOptimised/core/pathLookups';
import { hexToTint, particleRandom } from 'app/views/home/webgl/particlesOptimised/departments/departments.types';

import type { ParticleCore, PoolRange } from 'app/views/home/webgl/particlesOptimised/core/ParticleCore';
import type { DepartmentDeps } from 'app/views/home/webgl/particlesOptimised/departments/departments.types';
import type { DepartmentScene } from 'app/views/home/webgl/webgl.types';

//Script groups (cancellable batches — see ScriptList):
const GROUP_COUNTER = 'intro-counter'; //the per-particle opening-bloom counters
const GROUP_COMPLETE = 'intro-complete'; //the introComplete fade/deactivate timers

/**
 * IntroDepartment (particles-optimised) — FORK of
 * webgl/particles/departments/IntroDepartment.ts + the per-particle
 * IntroParticleProcessor, re-expressed as lane writes over the intro pool.
 * Formulas, constants and random distributions are ported verbatim (the
 * per-particle values are freshly rolled, same distributions — RNG streams
 * are not reproducible across the types and don't need to be).
 *
 * Structure mapping (reference -> here):
 *  - IntroDepartment.startAsActiveScene/setIntialState -> startAsActiveScene
 *    (same skipIntroClick guard);
 *  - IntroParticleProcessor.startState/mouseOver/mouseOut/introComplete ->
 *    the private per-lane methods; the reference's `index < 500` guards
 *    cover the whole 500-particle pool, so plain pool loops are equivalent;
 *  - ParticleCounter (opening bloom, millsecsToFrames(1100 + rand*400)) ->
 *    ScriptList entries in GROUP_COUNTER; introComplete's
 *    `counter.active = false` sweep -> cancelGroup;
 *  - introComplete's native setTimeouts -> ScriptList frame-domain entries
 *    (ms * 60 / 1000 — identical at 60fps, pausable; intended deviation);
 *  - tick's mousedown repell.reducePower loop is NOT ported: startState
 *    leaves the repell behaviour INACTIVE (reference line: repell.active =
 *    false, never re-enabled for intro), so the power writes are never read
 *    — dead choreography, dropped;
 *  - the sprite blendMode = 'add' every particle gets in startState lives
 *    container-level on this pool's renderer (equivalent: whole pool).
 *
 * Coordinate note: reference intro particles live in the field's ROOT
 * particle container (HomeField's particleFactory has no intro range check,
 * so indexes 0-499 fall through to the default container) — NOT in the icon
 * container. Their positions are plain disableTimelineX world px. The
 * facade mounts this pool's renderer on the root container accordingly.
 * The department still positions the icon container per tick while active
 * (reference _positionParticleContainer — it keeps the HOME container x /
 * particleContainersX.home maintained during the intro phase).
 */
export default class IntroDepartment implements DepartmentScene {
	private _deps: DepartmentDeps;
	private _core: ParticleCore;
	private _range: PoolRange;
	private _active: boolean = false;
	private _resizeTimeout: ReturnType<typeof setTimeout> = null;

	constructor(deps: DepartmentDeps) {
		this._deps = deps;
		this._core = deps.core;
		this._range = deps.core.pool('intro');
	}

	//DepartmentScene surface
	//-----------------------------------------------------------------------------------------------

	public tick(): void {
		if (!this._active) {
			return;
		}

		//Reference _positionParticleContainer (the facade already computed
		//the shared value this frame):
		this._deps.stage.iconContainer.x = this._deps.context.homeContainerX;
	}

	/** Intro becomes the active scene (deferred create() continuation). */
	public startAsActiveScene(): void {
		//Deeplink/dev-harness loads skip the intro click — the intro pool
		//never shows (reference guard, verbatim):
		if ((window as any).skipIntroClick) {
			return;
		}

		this._active = true;
		this._setParticlesStartState();
	}

	public stopAsActiveScene(): void {
		this._active = false;
		//Reference stops here too (its deactivation loop sits after an
		//unconditional `return` — dead code, not ported).
	}

	public startSceneDrag(): void {}

	public endSceneDrag(): void {}

	/** Reference resize: recenter now + a 100ms-debounced second pass. */
	public resize(): void {
		this._recenter();

		if (this._resizeTimeout) {
			clearTimeout(this._resizeTimeout);
		}

		this._resizeTimeout = setTimeout(() => {
			this._resizeTimeout = null;
			this._recenter();
		}, 100);
	}

	public setupForWork(): void {}

	public showFromWork(): void {}

	public fromWorkToHome(): void {}

	//Intro handshake (facade routes HomeWebGL.introHover/introOut/introComplete)
	//-----------------------------------------------------------------------------------------------

	/** IntroParticleProcessor.mouseOver per particle. */
	public mouseOver(): void {
		const core = this._core;

		for (let i = this._range.start; i < this._range.end; i++) {
			core.tint[i] = hexToTint(Math.random() < 0.5 ? HomeColours.getRandomColour(HomeColours.greenBlues) : '#ffffff');

			if (Math.random() < 0.09) {
				core.orbitPath.expandTarget[i] = 0.9 + Math.random() * 0.65;
			} else {
				core.orbitPath.expandTarget[i] = 0.9 + Math.random() * 0.3;
			}

			core.orbitPath.speedMulTarget[i] = 2 + Math.random() * 5;

			core.tAlpha[i] = particleRandom(0.1, 0.45);

			if (Math.random() < 0.95) {
				core.tScale[i] = particleRandom(0.03, 0.45);
			} else {
				core.tScale[i] = particleRandom(0.07, 0.9);
			}

			core.easeScale[i] = 20;
		}
	}

	/** IntroParticleProcessor.mouseOut per particle. */
	public mouseOut(): void {
		const core = this._core;

		for (let i = this._range.start; i < this._range.end; i++) {
			core.tint[i] = hexToTint(Math.random() < 0.3 ? HomeColours.getRandomColour(HomeColours.greenBlues) : '#ffffff');

			if (Math.random() < 0.35) {
				core.orbitPath.expandTarget[i] = 1;
			} else {
				core.orbitPath.expandTarget[i] = 0.8;
			}

			core.orbitPath.speedMulTarget[i] = particleRandom(1, 2);

			if (Math.random() < 0.9) {
				core.tScale[i] = particleRandom(0.07, 0.25);
				core.tAlpha[i] = particleRandom(0.1, 0.5);
			} else {
				core.tScale[i] = particleRandom(0.07, 0.5);
				core.tAlpha[i] = particleRandom(0.1, 0.1);
			}

			core.easeScale[i] = 10;
			core.easeAlpha[i] = 10;
		}
	}

	/** IntroParticleProcessor.introComplete per particle: orbit balloons out
	 *  (expand 2..7), speed eases to 0, alpha/scale fade, then deactivate. */
	public introComplete(): void {
		const core = this._core;
		const scripts = this._deps.scripts;

		//Reference: _p.counter.active = false on every particle first:
		scripts.cancelGroup(GROUP_COUNTER);

		//Config.mobileBreakpoint (800) — NOT the 1200 the center-x uses:
		const mobile = device.innerWidth < 800;
		const maxExpand = mobile ? 2 : 4;
		const maxExpandBig = mobile ? 2.5 : 5;
		const maxScale = mobile ? 0.22 : 0.17;

		const deactivate = (lane: number) => {
			core.stateFlags[lane] &= ~FLAG_ACTIVE;
		};
		const fadeOut = (lane: number) => {
			core.tAlpha[lane] = 0;
			core.tScale[lane] = 0;
		};

		for (let i = this._range.start; i < this._range.end; i++) {
			core.easeX[i] = 30;
			core.easeY[i] = 30;
			core.easeScale[i] = 30;
			core.easeAlpha[i] = 30;

			if (Math.random() < 0.5) {
				core.orbitPath.expandTarget[i] = 2 + Math.random() * maxExpandBig;
				core.orbitPath.speedMulTarget[i] = 0;
				core.tAlpha[i] = 0;
				core.tScale[i] = 0;

				scripts.addMs(i, 1000 + Math.random() * 1000, deactivate, false, GROUP_COMPLETE);
			} else {
				core.orbitPath.expandTarget[i] = 2 + Math.random() * maxExpand;
				core.orbitPath.speedMulTarget[i] = 0;
				core.tAlpha[i] = 0.2 + Math.random() * 0.2;
				core.tScale[i] = 0.06 + Math.random() * maxScale;

				scripts.addMs(i, 1000 + Math.random() * 600, fadeOut, false, GROUP_COMPLETE);
				scripts.addMs(i, 2000 + Math.random() * 1000, deactivate, false, GROUP_COMPLETE);
			}
		}
	}

	//Private
	//-----------------------------------------------------------------------------------------------

	/** Reference _setParticlesStartState -> setIntialState -> startState. */
	private _setParticlesStartState(): void {
		const isMobile = device.innerWidth < 1200;

		//The reference's `index < 500` guard spans the whole 500-slot pool:
		for (let i = this._range.start; i < this._range.end; i++) {
			this._startState(i, isMobile);
		}
	}

	/** IntroParticleProcessor.centerPosition + position.set(pos, true):
	 *  hard reset in disableTimelineX mode — target = absX = relX *
	 *  container.w (the reference hard path adds no xOffset), value snaps. */
	private _centerPosition(lane: number): void {
		const core = this._core;
		const sizes = this._deps.getSizes();
		const isMobile = device.innerWidth < 1200;

		core.relX[lane] = isMobile ? 0.125555 : 0.1666;
		core.relY[lane] = 0.5;
		core.xOffset[lane] = 0;

		core.tx[lane] = core.pTx[lane] = core.relX[lane] * sizes.virtualWidth;
		core.ty[lane] = core.pTy[lane] = core.relY[lane] * sizes.innerHeight;
		core.x[lane] = core.tx[lane];
		core.y[lane] = core.ty[lane];
	}

	private _recenter(): void {
		for (let i = this._range.start; i < this._range.end; i++) {
			this._centerPosition(i);
		}
	}

	/** IntroParticleProcessor.startState, lane-for-line. */
	private _startState(lane: number, isMobile: boolean): void {
		const core = this._core;
		const orbit = core.orbitPath;
		const circleLength = getPathLookups()[LOOKUP_CIRCLE].length;

		//deactivateAllBehaviours(true): everything off, position stays. The
		//mask rebuild also clears breathing/repell/paths from any prior life:
		core.behaviourMask[lane] = BEHAVIOUR_PROGRESS_PARALLAX;

		//activate():
		core.stateFlags[lane] |= FLAG_ACTIVE;

		//position.disableTimelineX = true:
		core.behaviourMask[lane] |= BEHAVIOUR_DISABLE_TIMELINE_X;

		this._centerPosition(lane);

		//sprite.blendMode = 'add' — container-level on this pool's renderer.

		//alpha = targetAlpha = 0; scale = targetScale = 0:
		core.alpha[lane] = core.tAlpha[lane] = 0;
		core.scale[lane] = core.tScale[lane] = 0;

		//Repell CONFIGURED but left inactive (no BEHAVIOUR_REPELL bit) — the
		//reference sets repell.active = false and never re-enables it for
		//intro; the config writes are kept for state parity:
		core.repellType[lane] = REPELL_PUSH;
		core.repellRadius[lane] = 20;
		core.repellPower[lane] = 10 + Math.random() * 20;

		const ease = 1 + Math.random() * 7;
		core.easeX[lane] = ease;
		core.easeY[lane] = ease;
		core.easeScale[lane] = 10;
		core.easeAlpha[lane] = 10;

		//setupPath(orbitPath, { x: [70,10], y: [70,10], speed: [0.02,0.6],
		//scale: [0.05,0.2], alpha: [0.03,0.2], randomise: { unsync: true } }):
		core.behaviourMask[lane] |= BEHAVIOUR_PATH_ORBIT;
		orbit.lookupId[lane] = LOOKUP_CIRCLE;
		orbit.xMul[lane] = 70 + Math.random() * 10;
		orbit.yMul[lane] = 70 + Math.random() * 10;
		orbit.speed[lane] = 0.02 + Math.random() * 0.6;
		//alphaActive: the reference alphaRange setter/branch bodies are empty
		//— nothing to port. scaleActive goes true here then FALSE below
		//(reference line order), so only the range lanes persist:
		orbit.scaleMin[lane] = 0.05;
		orbit.scaleMax[lane] = 0.2;
		orbit.dir[lane] = 1;
		//randomise(unsync = true):
		orbit.idx[lane] = Math.random() * (circleLength - 1);
		orbit.scaleIdx[lane] = Math.random() * (circleLength - 1);

		//expandMuliplier target 0 — the orbit contribution eases 1 -> 0, then
		//the counter below pops it back out (the opening bloom):
		orbit.expandTarget[lane] = 0;
		//scaleAffect = false (no spaces-zone scaling on the intro orbit):
		orbit.flags[lane] &= ~PATH_FLAG_SCALE_AFFECT;
		orbit.speedMulTarget[lane] = particleRandom(1, 2);

		//counter.millsecsToFrames(1100 + rand*400) + onTrigger (bloom):
		this._deps.scripts.addMs(
			lane,
			1100 + Math.random() * 400,
			(l: number) => this._bloomTrigger(l, isMobile),
			false,
			GROUP_COUNTER
		);

		orbit.flags[lane] &= ~PATH_FLAG_SCALE_ACTIVE;

		core.tint[lane] = hexToTint(
			Math.random() < 0.3 ? HomeColours.getRandomColour(HomeColours.greenBlues) : '#ffffff'
		);
	}

	/** startState's counter.onTrigger closure (the opening bloom). */
	private _bloomTrigger(lane: number, isMobile: boolean): void {
		const core = this._core;

		//Reference writes 1 then immediately overwrites via the 0.35 split:
		if (Math.random() < 0.35) {
			core.orbitPath.expandTarget[lane] = 1;
		} else {
			core.orbitPath.expandTarget[lane] = 0.8;
		}

		if (isMobile) {
			core.tAlpha[lane] = particleRandom(0.2, 0.35);

			if (Math.random() < 0.95) {
				core.tScale[lane] = particleRandom(0.07, 0.35);
			} else {
				core.tScale[lane] = particleRandom(0.07, 0.47);
			}
		} else {
			if (Math.random() < 0.9) {
				core.tScale[lane] = particleRandom(0.07, 0.25);
				core.tAlpha[lane] = particleRandom(0.1, 0.5);
			} else {
				core.tScale[lane] = particleRandom(0.07, 0.5);
				core.tAlpha[lane] = particleRandom(0.1, 0.1);
			}
		}
	}
}
